'use client';

import React, { useMemo } from 'react';

import ReactPlayer from 'react-player';

import {
    VideoPlayer,
    VideoPlayerContent,
    VideoPlayerControlBar,
    VideoPlayerFullscreenButton,
    VideoPlayerMuteButton,
    VideoPlayerPlayButton,
    VideoPlayerSeekBackwardButton,
    VideoPlayerSeekForwardButton,
    VideoPlayerTimeDisplay,
    VideoPlayerTimeRange,
    VideoPlayerVolumeRange,
} from '@/components/ui/video-player';
import { cn } from '@/lib/utils';

export type VideoBlockProps = {
    sourceType: 'external' | 'upload';
    url?: string;
    media?: { url: string }; // Payload автоматически добавляет поле url
    caption?: string;
    blockType: 'video';
    align?: 'left' | 'center' | 'right';
};

type Props = VideoBlockProps & {
    className?: string;
};

const VideoEmbed: React.FC<Props> = ({ sourceType, url, media, caption, align = 'center', className }) => {
    const localUrl = useMemo(() => media?.url || '', [media?.url]);

    // float/width классы:
    // - на мобильных (по умолчанию) делаем full-width (чтобы не ломать UX)
    // - на sm+ делаем float если left|right
    const floatClass =
        align === 'left'
            ? 'lg:float-left lg:mr-4 lg:mb-4' // плавает влево на sm+
            : align === 'right'
              ? 'lg:float-right lg:ml-4 lg:mb-4' // плавает вправо на sm+
              : 'mx-auto'; // центр на всех размерах

    const widthClass =
        align === 'center'
            ? 'w-full lg:max-w-[900px]' // центрированный крупный блок
            : 'w-full md:max-w-[400px] sm:max-w-[520px] md:max-w-[600px]'; // плавающий — ограниченная ширина на sm+

    if (sourceType === 'external') {
        return (
            <figure suppressHydrationWarning className={cn(floatClass, widthClass, 'mb-4', className)}>
                <div
                    className={cn(
                        'bg-secondary relative aspect-video w-full overflow-hidden rounded-lg border',
                        'flex-2'
                    )}
                >
                    <div className="relative h-full w-full">
                        <ReactPlayer src={url} width="100%" height="100%" controls className="absolute top-0 left-0" />
                    </div>
                </div>
                {caption && (
                    <figcaption className="text-muted-foreground mt-2 text-center text-sm">{caption}</figcaption>
                )}
            </figure>
        );
    }

    return (
        <figure suppressHydrationWarning className={cn(floatClass, widthClass, className)}>
            <VideoPlayer className="relative aspect-video w-full overflow-hidden rounded-lg border bg-black/90">
                <VideoPlayerContent
                    crossOrigin="anonymous"
                    preload="metadata"
                    controlsList="nodownload"
                    playsInline
                    slot="media"
                    src={localUrl}
                    draggable={false}
                    className="absolute top-0 bottom-0 left-1/2 h-full lg:w-auto -translate-x-1/2 transform object-contain"
                />
                <VideoPlayerControlBar>
                    <VideoPlayerPlayButton />
                    <VideoPlayerSeekBackwardButton />
                    <VideoPlayerSeekForwardButton />
                    <VideoPlayerTimeRange />
                    <VideoPlayerTimeDisplay showDuration />
                    <VideoPlayerMuteButton />
                    <VideoPlayerVolumeRange />
                    <VideoPlayerFullscreenButton />
                </VideoPlayerControlBar>
            </VideoPlayer>

            {caption && <figcaption className="text-muted-foreground mt-2 text-center text-sm">{caption}</figcaption>}
        </figure>
    );
};

export default VideoEmbed;
